import { useTheme } from '../contexts/ThemeContext';
import { HeaderFormat, TemplateButton } from '../types/template';

interface TemplatePreviewProps {
  header?: {
    type: HeaderFormat;
    text?: string;
    mediaUrl?: string; // URL para preview de imagem/vídeo
  };
  body: string;
  footer?: string;
  buttons?: TemplateButton[];
}

export function TemplatePreview({ header, body, footer, buttons }: TemplatePreviewProps) {
  const { theme } = useTheme();

  const highlightVariables = (text: string) => {
    if (!text) return text;
    
    // Destacar variáveis {{1}}, {{2}}, etc
    return text.split(/(\{\{\d+\}\})/g).map((part, index) => {
      if (/\{\{\d+\}\}/.test(part)) {
        return (
          <span key={index} className="bg-theme-warning/20 text-theme-text px-1 rounded">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div 
      className="rounded-lg p-6 relative overflow-hidden"
      style={{
        backgroundImage: 'url(/background-whatsapp.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay escuro para temas dark */}
      {theme === 'dark' && (
        <div className="absolute inset-0 bg-black/40 rounded-lg" />
      )}

      {/* Info */}
      <div className="mb-4 relative z-10">
        <p className="text-sm font-medium text-theme-text">
          Pré-visualização no estilo WhatsApp
        </p>
      </div>

      {/* WhatsApp-style message bubble */}
      <div className="bg-theme-card rounded-lg shadow-md overflow-hidden max-w-md mx-auto relative z-10">
        {/* Header */}
        {header && (
          <div>
            {header.type === 'TEXT' && header.text && (
              <div className="px-4 pt-4 pb-2">
                <p className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                  {highlightVariables(header.text)}
                </p>
              </div>
            )}

            {header.type === 'IMAGE' && (
              <div className="bg-gray-200 h-48 flex items-center justify-center overflow-hidden">
                {header.mediaUrl ? (
                  <img 
                    src={header.mediaUrl} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm">Imagem será exibida aqui</p>
                  </div>
                )}
              </div>
            )}

            {header.type === 'VIDEO' && (
              <div className="bg-gray-900 h-48 flex items-center justify-center overflow-hidden">
                {header.mediaUrl ? (
                  <video 
                    src={header.mediaUrl} 
                    className="w-full h-full object-cover"
                    controls
                  />
                ) : (
                  <div className="text-center text-gray-300">
                    <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm">Vídeo será exibido aqui</p>
                  </div>
                )}
              </div>
            )}

            {header.type === 'DOCUMENT' && (
              <div className="bg-blue-50 p-4 flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Documento</p>
                  <p className="text-sm text-gray-600">PDF será exibido aqui</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Body */}
        <div className="px-4 py-3">
          {body ? (
            <p className="text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">
              {highlightVariables(body)}
            </p>
          ) : (
            <p className="text-gray-400 dark:text-gray-500 italic">
              Digite o texto da mensagem...
            </p>
          )}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-4 pb-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {footer}
            </p>
          </div>
        )}

        {/* Timestamp */}
        <div className="px-4 pb-2 flex justify-end">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>

        {/* Buttons */}
        {buttons && buttons.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-600">
            {buttons.map((button, index) => (
              <button
                key={index}
                className="w-full py-3 text-center text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors font-medium flex items-center justify-center gap-2 border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                disabled
              >
                {button.type === 'URL' && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                )}
                {button.type === 'PHONE_NUMBER' && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                )}
                {button.text || 'Botão sem texto'}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      {body.includes('{{') && (
        <div className={`mt-4 p-3 rounded-lg relative z-10 ${
          theme === 'dark' ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'
        }`}>
          <p className={`text-xs ${
            theme === 'dark' ? 'text-yellow-300' : 'text-yellow-800'
          }`}>
            💡 As variáveis destacadas ({'{'}1{'}'}, {'{'}2{'}'}, etc) serão substituídas pelos valores reais ao enviar
          </p>
        </div>
      )}
    </div>
  );
}

